import { PromptData } from "./iaPromptExtractor";

export function buildSystemPromptText(data: PromptData): string {
  const { sav, contract, warranty, beneficiary, vehicle } = data;

  // `warranty.options` est une colonne legacy dont le contenu a changé de sens :
  // elle portait des libellés de prestations, elle porte désormais les
  // identifiants des pièces couvertes, et peut être nulle. Injecter cela tel
  // quel donnait au modèle une ligne « Options de garantie : 123, 456 » —
  // du bruit qu'il pouvait restituer au conseiller. On ne garde donc que les
  // valeurs réellement lisibles, et l'accès est protégé contre le null.
  const rawWarrantyOptions: unknown = warranty?.options;
  const warrantyOptionLabels: string[] = Array.isArray(rawWarrantyOptions)
    ? rawWarrantyOptions.filter(
        (option): option is string => typeof option === 'string' && !/^\d+$/.test(option),
      )
    : [];

  const lines = [
    `Tu es un assistant SAV. Voici toutes les informations contextuelles à propos du dossier SAV sur lequel tu dois répondre. Utilise ces informations pour donner des réponses précises, professionnelles et adaptées au contexte.`,
    '',
    '========================',
    'INFORMATIONS SAV',
    '------------------------',
    `- Numéro SAV : ${sav.id}`,
    `- Statut (code) : ${sav.status}`,
    `- Statut (libellé) : ${sav.statusLabel}`,
    `- Date de déclaration : ${formatDate(sav.dateDeclaration)}`,
    `- Date du de la panne : ${formatDate(sav.dateSinistre)}`,
    `- Date de création : ${formatDate(sav.dateCreation)}`,
    `- Date modif. déclarant : ${formatDate(sav.dateModificationDeclarant)}`,
    `- Date modif. opérateur : ${formatDate(sav.dateModificationOperateur)}`,
    `- Jours depuis la déclaration : ${sav.joursDepuisDeclaration ?? 'N/A'}`,
    `- Jours restants de garantie : ${sav.joursRestantsGarantie ?? 'N/A'}`,
    `- Pourcentage de garantie écoulée : ${sav.pourcentageGarantieEcoulee ?? 'N/A'}%`,
    `- Tags : [${sav.tags.join(', ')}]`,
    sav.description ? `- Description : ${sav.description}` : '',
    sav.montantSeller !== undefined ? `- Montant pris en charge par le vendeur : ${sav.montantSeller} €` : '',
    sav.montantCommercial !== undefined ? `- Montant commercial : ${sav.montantCommercial} €` : '',
    sav.montantBeneficiary !== undefined ? `- Montant à la charge du bénéficiaire : ${sav.montantBeneficiary} €` : '',
    sav.currentKm !== undefined ? `- Kilométrage actuel : ${sav.currentKm} km` : '',
    sav.plafondKmAlerte ? `- Alerte kilométrique : ${sav.plafondKmAlerte}` : '',
    sav.plafondFinancierAlerte ? `- Alerte plafond financier : ${sav.plafondFinancierAlerte}` : '',
    sav.plafondKmRestant !== undefined ? `- Km restants avant plafond : ${sav.plafondKmRestant} km` : '',
    sav.plafondKmDepasse !== undefined ? `- Plafond kilométrique dépassé : ${sav.plafondKmDepasse ? 'Oui' : 'Non'}` : '',
    sav.plafondFinancierRestant !== undefined ? `- Montant restant avant plafond financier : ${sav.plafondFinancierRestant} €` : '',
    sav.plafondFinancierDepasse !== undefined ? `- Plafond financier dépassé : ${sav.plafondFinancierDepasse ? 'Oui' : 'Non'}` : '',
    sav.preAnalyseSynthese ? `- Synthèse pré-analyse : ${sav.preAnalyseSynthese}` : '',
    sav.formValues && sav.formValues.length > 0 ?
      [
        '- Champs complémentaires :',
        ...sav.formValues.map(f => `    • ${f.label} : ${f.value && f.value !== '' ? f.value : 'Non renseigné'}`)
      ].join('\n')
      : '',
    '',
    '========================',
    'CONTRAT & GARANTIE',
    '------------------------',
    `- Contrat : ${contract.label}`,
    `- Date de souscription : ${formatDate(contract.dateSouscription)}`,
    `- Date de fin : ${formatDate(contract.dateEnd)}`,
    `- Société : ${contract.companyName}`,
    `- Type de vendeur : ${contract.vendorType}`,
    contract.plafondFinancier !== undefined ? `- Plafond financier : ${contract.plafondFinancier} €` : '',
    contract.limiteKm !== undefined ? `- Limite kilométrique : ${contract.limiteKm} km` : '',
    warranty ? `- Garantie : ${warranty.name} ${warranty.duration} mois` : '',
    warrantyOptionLabels.length > 0 ? `- Options de garantie : ${warrantyOptionLabels.join(', ')}` : '',
    contract.consignes ? `- Consignes SAV : ${contract.consignes}` : '',
    contract.savManager ? `- Correspondant pour le SAV du côté vendeur : ${[contract.savManager.firstName, contract.savManager.lastName].filter(Boolean).join(' ')}${contract.savManager.email ? `, Email : ${contract.savManager.email}` : ''}${contract.savManager.phone ? `, Tel : ${contract.savManager.phone}` : ''}` : '',
    '',
    '========================',
    'BÉNÉFICIAIRE',
    '------------------------',
    `- Nom : ${beneficiary.firstName} ${beneficiary.lastName}`,
    `- Adresse : ${beneficiary.address}, ${beneficiary.cp} ${beneficiary.city}`,
    `- Email : ${beneficiary.email}`,
    `- Téléphone : ${beneficiary.phoneNumber}`,
    beneficiary.phoneNumber2 ? `- Téléphone (fixe) : ${beneficiary.phoneNumber2}` : '',
    '',
    vehicle && (
      [
        '========================',
        'VÉHICULE',
        '------------------------',
        vehicle.registration ? `- Immatriculation : ${vehicle.registration}` : '',
        vehicle.brand ? `- Marque : ${vehicle.brand}` : '',
        vehicle.model ? `- Modèle : ${vehicle.model}` : '',
        vehicle.fiscalPower ? `- Puissance fiscale : ${vehicle.fiscalPower}` : '',
        vehicle.entryIntoService ? `- Mise en circulation : ${vehicle.entryIntoService}` : '',
        vehicle.kmStart ? `- Kilométrage au jour de la vente : ${vehicle.kmStart}` : '',
        ''
      ].join('\n')
    ),
    '========================',
    // Section Réparateur
    data.reparateur ? [
      '========================',
      'RÉPARATEUR CHOISI',
      '------------------------',
      `- Nom : ${data.reparateur.nom ?? 'N/A'}`,
      `- Adresse : ${data.reparateur.adresse ?? 'N/A'}`,
      `- CP : ${data.reparateur.cp ?? 'N/A'}`,
      `- Ville : ${data.reparateur.ville ?? 'N/A'}`,
      `- Email : ${data.reparateur.email ?? 'N/A'}`,
      `- Téléphone : ${data.reparateur.telephone ?? 'N/A'}`,
      `- Est réparateur agréé : ${data.reparateur.isReparator ? 'Oui' : 'Non'}`,
      ''
    ].join('\n') : '',
    // Section Devis
    data.devis ? [
      '========================',
      'DEVIS ASSOCIÉ',
      '------------------------',
      `- Numéro de devis : ${data.devis.id ?? 'N/A'}`,
      `- Libellé : ${data.devis.label ?? 'N/A'}`,
      `- Date de création : ${formatDate(data.devis.dateCreation)}`,
      `- Lien de téléchargement : ${data.devis.downloadUrl ?? 'N/A'}`,
      ''
    ].join('\n') : '',
    // Section Bon pour accord/refus
    data.bonPourAccord ? [
      '========================',
      'BON POUR ACCORD / REFUS',
      '------------------------',
      `- Décision : ${data.bonPourAccord.decision ?? 'N/A'}`,
      `- Date : ${formatDate(data.bonPourAccord.date)}`,
      `- Commentaire : ${data.bonPourAccord.comment ?? 'N/A'}`,
      `- Montant vendeur : ${data.bonPourAccord.montantVendeur ?? 'N/A'} €` ,
      `- Montant commercial : ${data.bonPourAccord.montantCommercial ?? 'N/A'} €` ,
      data.bonPourAccord.details && data.bonPourAccord.details.length > 0 ?
        ['- Détails :', ...data.bonPourAccord.details.map(d => `    • ${d.label} : ${d.montant} € x ${d.quantite} (Total : ${d.montantTotal} €)`)].join('\n') : '',
      `- Envoyé au vendeur : ${data.bonPourAccord.sentToSeller ? 'Oui' : 'Non'}${data.bonPourAccord.sentToSellerDateAt ? ` (le ${formatDate(data.bonPourAccord.sentToSellerDateAt)})` : ''}`,
      `- Envoyé au bénéficiaire : ${data.bonPourAccord.sentToCustomer ? 'Oui' : 'Non'}${data.bonPourAccord.sentToCustomerDateAt ? ` (le ${formatDate(data.bonPourAccord.sentToCustomerDateAt)})` : ''}`,
      `- Envoyé au réparateur : ${data.bonPourAccord.sentToRepairer ? 'Oui' : 'Non'}${data.bonPourAccord.sentToRepairerDateAt ? ` (le ${formatDate(data.bonPourAccord.sentToRepairerDateAt)})` : ''}`,
      `- Signé par le bénéficiaire : ${data.bonPourAccord.signedByCustomer ? 'Oui' : 'Non'}${data.bonPourAccord.signedByCustomerDateAt ? ` (le ${formatDate(data.bonPourAccord.signedByCustomerDateAt)})` : ''}`,
      `- Signé par le vendeur : ${data.bonPourAccord.signedBySeller ? 'Oui' : 'Non'}${data.bonPourAccord.signedBySellerDateAt ? ` (le ${formatDate(data.bonPourAccord.signedBySellerDateAt)})` : ''}`,
      ''
    ].join('\n') : '',
    // Section Historique des échanges avec le bénéficiaire
    data.historiqueBeneficiaire && data.historiqueBeneficiaire.length > 0 ? [
      '========================',
      'HISTORIQUE DES ÉCHANGES AVEC LE BÉNÉFICIAIRE',
      '------------------------',
      ...data.historiqueBeneficiaire.slice(-20).map(m =>
        `[${formatDateTime(m.date)}] ${m.auteur} : ${m.texte}`
      ),
      ''
    ].join('\n') : '',
    // Section Règles (déplacée tout en bas)
    [
      '========================',
      'Règles :',
      '- Ne jamais inventer d’informations qui ne figurent pas ci-dessus.',
      '- Si une information est manquante, indique-le ou demande une précision.',
      '- Sois synthétique et professionnel.',
      '',
      'Règles complémentaires :',
      '- Les statuts internes (ex : “en attente gestionnaire”, “en cours”, etc.) ne doivent jamais être communiqués, expliqués ou mentionnés dans un message destiné au bénéficiaire ou à un interlocuteur externe.',
      '- Si tu dois rédiger un message pour l’extérieur, utilise un langage professionnel, neutre, et ne fais jamais référence à l’organisation interne ou à ses statuts.',
      '- Tu peux utiliser les statuts internes uniquement pour répondre à une question posée par un gestionnaire ou pour une analyse interne.',
      '- Si la demande concerne la rédaction d’un message à destination du bénéficiaire, adapte le ton et le contenu pour ne jamais révéler d’informations internes.',
      '- Tant que nous n’avons pas la décision du garant, ne pas donner de réponse sur le dossier',
    ].join('\n'),
  ];
  return lines.filter(Boolean).join('\n');
}

function formatDate(date: string | undefined): string {
  if (!date) return '';
  try {
    return new Date(date).toLocaleDateString('fr-FR');
  } catch {
    return date;
  }
}

function formatDateTime(date: string | undefined): string {
  if (!date) return '';
  try {
    return new Date(date).toLocaleString('fr-FR');
  } catch {
    return date;
  }
}